// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Tower Monitor Station
// Disposal site load estimation interface for tower monitors
// Quick truck check-in with fullness estimation and auto CY calc
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import { usePolling } from '../../hooks/usePolling';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  SimpleGrid, Slider, Textarea, TextInput, ActionIcon, Modal,
} from '../../components/gab/GabComponents';
import {
  IconTruck, IconCheck, IconRefresh, IconSearch,
  IconCamera, IconX, IconShieldCheck,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS, TICKET_STATUS_COLORS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { fetchTable, TABLES, FIELDS, updateRecord } from '../../lib/api-switch';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

async function uploadTicketPhoto(
  ticketId: number, fieldKey: string, _fileName: string, base64Raw: string, token?: string,
) {
  await updateRecord(TABLES.debrisLoadTickets, ticketId, [[fieldKey, base64Raw]], token);
}

const DEBRIS_TYPE_SELECT = Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

export default function MonitorStation() {
  const { activeIncident, user } = useAuthStore();
  const isAdmin = useIsAdmin();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    trucks, disposalSites,
    loadTrucks, loadDisposalSites, loadZones, updateDisposalSite,
  } = useDebrisStore();
  const {
    loadTickets,
    loadLoadTickets, createLoadTicket, updateLoadTicket,
  } = useDebrisOpsStore();

  // Monitor station state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [truckSearch, setTruckSearch] = useState('');
  const [selectedTruck, setSelectedTruck] = useState<string | null>(null);
  const [loadPct, setLoadPct] = useState(75);
  const [debrisType, setDebrisType] = useState<string | null>(null);
  const [unitCount, setUnitCount] = useState('');
  const [unitSubcategory, setUnitSubcategory] = useState('');
  const [checkInWeight, setCheckInWeight] = useState('');
  const [checkInWeightUnit, setCheckInWeightUnit] = useState('lbs');
  const [monitorNotes, setMonitorNotes] = useState('');
  const [selectedMonitor, setSelectedMonitor] = useState<string | null>(null);
  const [monitorOptions, setMonitorOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [loadPhotoData, setLoadPhotoData] = useState<{ data: string; name: string } | null>(null);

  const handleLoadPhoto = async (file: File | null) => {
    if (!file) return;
    const data = await fileToBase64(file);
    setLoadPhotoData({ data, name: file.name });
  };

  useEffect(() => {
    loadTrucks(); loadDisposalSites(); loadZones(); loadLoadTickets();
    // Load monitors for dropdown
    if (TABLES.debrisMonitors) {
      const FM = FIELDS.debrisMonitors;
      fetchTable(TABLES.debrisMonitors).then((rows: any[]) => {
        const opts = rows
          .filter((m: any) => String(m[FM.status] ?? 'active') === 'active')
          .map((m: any) => {
            const name = String(m[FM.fullName] ?? '');
            const company = String(m[FM.company] ?? '');
            return { value: name, label: company ? `${name} — ${company}` : name };
          });
        setMonitorOptions(opts);
        // Auto-select if logged-in user matches a monitor
        const userName = (user?.fullName ?? user?.email ?? '').toLowerCase();
        const match = opts.find(o => o.value.toLowerCase() === userName);
        if (match) setSelectedMonitor(match.value);
      }).catch(() => {});
    }
  }, [loadTrucks, loadDisposalSites, loadZones, loadLoadTickets]);
  usePolling(() => { loadLoadTickets(); loadTrucks(); }, 15000);

  const iSites = incidentId ? disposalSites : [];
  const iTrucks = incidentId ? trucks : [];
  const iTickets = incidentId ? loadTickets : [];

  const siteSelect = useMemo(() => iSites.map(s => ({ value: String(s.id), label: `${s.siteName} (${s.siteType})` })), [iSites]);
  const truckSelect = useMemo(() => {
    let list = iTrucks;
    if (truckSearch) {
      const q = truckSearch.toLowerCase();
      list = list.filter(t => t.truckNumber.toLowerCase().includes(q) || t.licensePlate.toLowerCase().includes(q));
    }
    return list.map(t => ({ value: String(t.id), label: `${t.truckNumber} — ${t.licensePlate} (${parseFloat(t.cubicYardCapacity || '0').toFixed(1)} CY)` }));
  }, [iTrucks, truckSearch]);

  const activeSite = useMemo(() => iSites.find(s => String(s.id) === selectedSite), [iSites, selectedSite]);
  const activeTruck = useMemo(() => iTrucks.find(t => String(t.id) === selectedTruck), [iTrucks, selectedTruck]);

  // Debris type config
  const debrisCfg = debrisType ? DEBRIS_TYPES[debrisType as DebrisType] : null;
  const measureMode = debrisCfg?.measurementMode || 'volumetric';

  // Auto-calculate CY (only relevant for volumetric)
  const calculatedCY = useMemo(() => {
    if (!activeTruck || measureMode !== 'volumetric') return 0;
    return (parseFloat(activeTruck.cubicYardCapacity || '0') * loadPct) / 100;
  }, [activeTruck, loadPct, measureMode]);

  // Site stats
  const siteTicketsToday = useMemo(() => {
    if (!selectedSite) return [];
    const today = new Date().toISOString().split('T')[0];
    return iTickets.filter(t =>
      t.disposalSiteId === selectedSite &&
      (t.disposalTime?.startsWith(today) || t.verifiedAt?.startsWith(today)),
    );
  }, [iTickets, selectedSite]);

  const siteTodayCY = useMemo(() =>
    siteTicketsToday.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0),
  [siteTicketsToday]);

  // Generate next ticket number
  const nextTicket = () => {
    const max = iTickets.reduce((m, t) => {
      const n = parseInt(t.ticketNumber?.replace(/\D/g, '') || '0');
      return n > m ? n : m;
    }, 0);
    return `T-${String(max + 1).padStart(5, '0')}`;
  };

  const handleCheckIn = async () => {
    if (!selectedSite || !selectedTruck || !debrisType) {
      notifications.show({ message: 'Select site, truck, and debris type', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
      const ticketNum = nextTicket();
      const ticketData: Record<string, string> = {
        incidentId,
        ticketNumber: ticketNum,
        truckId: selectedTruck,
        truckNumber: activeTruck?.truckNumber ?? '',
        contractorId: activeTruck?.contractorId ?? '',
        disposalSiteId: selectedSite,
        debrisType,
        measurementMode: measureMode,
        disposalTime: now,
        monitorName: selectedMonitor ?? user?.fullName ?? user?.email ?? '',
        verifiedAt: new Date().toISOString(),
        status: 'verified',
        notes: monitorNotes,
      };

      // Add measurement fields based on mode
      if (measureMode === 'volumetric') {
        ticketData.loadPercentage = String(loadPct);
        ticketData.calculatedCubicYards = calculatedCY.toFixed(1);
      } else if (measureMode === 'unit') {
        ticketData.unitCount = unitCount;
        ticketData.unitSubcategory = unitSubcategory;
      } else if (measureMode === 'weight') {
        ticketData.weight = checkInWeight;
        ticketData.weightUnit = checkInWeightUnit;
      }

      await createLoadTicket(ticketData as any);

      // Update site volume (only for volumetric)
      if (activeSite && measureMode === 'volumetric' && calculatedCY > 0) {
        const newVol = parseFloat(activeSite.currentVolume || '0') + calculatedCY;
        await updateDisposalSite(activeSite.id, { currentVolume: newVol.toFixed(1) });
      }

      // Upload load photo if captured — wait for store to reload first
      await loadLoadTickets();
      if (loadPhotoData) {
        const created = useDebrisOpsStore.getState().loadTickets.find(
          t => t.ticketNumber === ticketNum,
        );
        if (created?.id) {
          const token = useAuthStore.getState().token ?? undefined;
          await uploadTicketPhoto(
            created.id, FIELDS.debrisLoadTickets.photoLoadUrl,
            loadPhotoData.name, loadPhotoData.data.split(',')[1], token,
          ).catch(() => { /* photo upload is best-effort */ });
        }
      }

      const measureSummary = measureMode === 'volumetric'
        ? `${calculatedCY.toFixed(1)} CY`
        : measureMode === 'unit'
          ? `${unitCount} ${debrisCfg?.unitLabel || 'units'}`
          : `${checkInWeight} ${checkInWeightUnit}`;
      notifications.show({
        message: `Checked in: ${activeTruck?.truckNumber} — ${measureSummary} (verified)`,
        color: 'green',
      });

      // Reset form for next truck
      setSelectedTruck(null);
      setTruckSearch('');
      setLoadPct(75);
      setDebrisType(null);
      setUnitCount('');
      setUnitSubcategory('');
      setCheckInWeight('');
      setCheckInWeightUnit('lbs');
      setMonitorNotes('');
      setLoadPhotoData(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Check-in failed', color: 'red' });
    } finally { setSaving(false); }
  };

  // Tickets that need attention at this site
  const pendingTickets = useMemo(() => {
    if (!selectedSite) return iTickets.filter(t =>
      t.status === 'draft' || t.status === 'in_transit' || t.status === 'at_site'
    );
    return iTickets.filter(t =>
      (t.status === 'draft' || t.status === 'in_transit' || t.status === 'at_site') &&
      (!t.disposalSiteId || t.disposalSiteId === selectedSite)
    );
  }, [iTickets, selectedSite]);

  // Auto-scroll to pending section when new tickets arrive
  const pendingRef = useRef<HTMLDivElement>(null);
  const prevPendingCount = useRef(pendingTickets.length);
  useEffect(() => {
    if (pendingTickets.length > prevPendingCount.current && pendingRef.current) {
      pendingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevPendingCount.current = pendingTickets.length;
  }, [pendingTickets.length]);

  const siteTickets = useMemo(() => {
    if (!selectedSite) return [];
    return iTickets
      .filter(t => t.disposalSiteId === selectedSite)
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, 20);
  }, [iTickets, selectedSite]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Tower Monitor Station</Title>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          onClick={() => { loadTrucks(); loadLoadTickets(); loadDisposalSites(); }}>
          Refresh
        </Button>
      </Group>

      {/* Site selector */}
      <Paper p="md" radius="md" withBorder>
        <Select label="Active Disposal Site" data={siteSelect} value={selectedSite}
          onChange={v => setSelectedSite(v)} searchable size="md"
          placeholder="Select the site you're monitoring..." />

        {activeSite && (
          <SimpleGrid cols={{ base: 2, sm: 4 }} mt="md">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Site Type</Text>
              <Badge>{activeSite.siteType}</Badge>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Status</Text>
              <Badge color={activeSite.status === 'open' ? 'green' : activeSite.status === 'full' ? 'orange' : 'gray'}>
                {activeSite.status}
              </Badge>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Fill Level</Text>
              <Text fw={600}>
                {parseFloat(activeSite.currentVolume || '0').toLocaleString()} / {parseFloat(activeSite.capacityCubicYards || '0').toLocaleString()} CY
                {parseFloat(activeSite.capacityCubicYards || '0') > 0 && (
                  <Text span size="xs" c="dimmed"> ({((parseFloat(activeSite.currentVolume || '0') / parseFloat(activeSite.capacityCubicYards || '0')) * 100).toFixed(1)}%)</Text>
                )}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Today</Text>
              <Text fw={600}>{siteTicketsToday.length} loads &middot; {siteTodayCY.toLocaleString()} CY</Text>
            </div>
          </SimpleGrid>
        )}
      </Paper>

      {/* Pending tickets — always visible, not gated behind site selection */}
      {pendingTickets.length > 0 && (
        <Paper ref={pendingRef} p="md" radius="md" withBorder style={{ background: 'var(--mantine-color-yellow-0)', borderColor: '#f59e0b' }}>
          <Text size="sm" fw={700} c="#92400e" mb="xs">
            Pending Verification ({pendingTickets.length})
          </Text>
          <Stack gap={4}>
            {pendingTickets.slice(0, 15).map(t => (
              <Group key={t.id} justify="space-between" p={4}
                style={{ borderRadius: 4, cursor: 'pointer', background: 'white' }}
                onClick={() => window.open(`/debris/verify/${t.id}`, '_blank')}>
                <div>
                  <Text size="sm" fw={500}>{t.ticketNumber} — {t.truckNumber || 'Unknown truck'}</Text>
                  <Text size="xs" c="dimmed">
                    {DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType}
                    {t.pickupAddress ? ` — ${t.pickupAddress}` : ''}
                  </Text>
                </div>
                <Badge size="xs" color={t.status === 'draft' ? 'gray' : t.status === 'in_transit' ? 'blue' : 'yellow'}>
                  {t.status?.replace(/_/g, ' ')}
                </Badge>
              </Group>
            ))}
          </Stack>
          <Text size="xs" c="dimmed" mt="xs">Click a ticket to open the full verification form</Text>
        </Paper>
      )}

      {selectedSite && (
        <SimpleGrid cols={{ base: 1, lg: 2 }}>
          {/* Check-in form */}
          <Paper p="lg" radius="md" withBorder>
            <Text fw={700} mb="md" size="lg">Quick Check-In</Text>
            <Text size="xs" c="dimmed" mb="md">
              For trucks arriving without a pre-created ticket. Creates a verified ticket immediately.
            </Text>

            <Stack gap="md">
              <Select label="Select Truck" data={truckSelect} value={selectedTruck}
                onChange={v => setSelectedTruck(v)} searchable size="md"
                placeholder="Search by truck # or license plate..."
                searchValue={truckSearch}
                onSearchChange={setTruckSearch}
                leftSection={<IconSearch size={16} />}
              />

              {activeTruck && (
                <Paper p="sm" radius="sm" withBorder style={{ background: 'var(--mantine-color-blue-0)' }}>
                  <SimpleGrid cols={3}>
                    <div><Text size="xs" c="dimmed">Truck #</Text><Text fw={600}>{activeTruck.truckNumber}</Text></div>
                    <div><Text size="xs" c="dimmed">Plate</Text><Text fw={600}>{activeTruck.licensePlate}</Text></div>
                    <div><Text size="xs" c="dimmed">Capacity</Text><Text fw={600}>{parseFloat(activeTruck.cubicYardCapacity || '0').toFixed(1)} CY</Text></div>
                    <div><Text size="xs" c="dimmed">Type</Text><Text>{activeTruck.truckType}</Text></div>
                    <div><Text size="xs" c="dimmed">Dimensions</Text><Text>{activeTruck.lengthFt}×{activeTruck.widthFt}×{activeTruck.heightFt} ft</Text></div>
                    <div><Text size="xs" c="dimmed">Status</Text><Badge size="sm">{activeTruck.status}</Badge></div>
                  </SimpleGrid>
                </Paper>
              )}

              <Select label="Debris Type" data={DEBRIS_TYPE_SELECT} value={debrisType}
                onChange={v => { setDebrisType(v); setUnitCount(''); setUnitSubcategory(''); setCheckInWeight(''); }}
                searchable size="md" />

              {/* Adaptive measurement based on debris type */}
              {measureMode === 'volumetric' && (
                <>
                  <div>
                    <Text size="sm" fw={600} mb={4}>Load Estimate: {loadPct}%</Text>
                    <Slider value={loadPct} onChange={setLoadPct}
                      min={0} max={100} step={5} size="lg"
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 25, label: '25%' },
                        { value: 50, label: '50%' },
                        { value: 75, label: '75%' },
                        { value: 100, label: '100%' },
                      ]}
                      styles={{ markLabel: { fontSize: 11 } }}
                    />
                  </div>
                  {activeTruck && (
                    <Paper p="md" radius="sm" withBorder style={{ background: 'var(--mantine-color-green-0)', textAlign: 'center' }}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Calculated Volume</Text>
                      <Text size="xl" fw={800} c="green">{calculatedCY.toFixed(1)} CY</Text>
                      <Text size="xs" c="dimmed">
                        {parseFloat(activeTruck.cubicYardCapacity || '0').toFixed(1)} CY × {loadPct}%
                      </Text>
                    </Paper>
                  )}
                </>
              )}

              {measureMode === 'unit' && (
                <SimpleGrid cols={debrisCfg?.subcategories ? 2 : 1} spacing="sm">
                  <TextInput label={`Quantity (${debrisCfg?.unitLabel || 'units'})`} type="number" min={0} size="md"
                    value={unitCount} onChange={e => setUnitCount(e.target.value)}
                    placeholder="e.g. 47" />
                  {debrisCfg?.subcategories && debrisCfg.subcategories.length > 0 && (
                    <Select label="Subcategory" size="md"
                      data={debrisCfg.subcategories.map(s => ({ value: s, label: s }))}
                      value={unitSubcategory || null}
                      onChange={v => setUnitSubcategory(v ?? '')}
                      searchable clearable />
                  )}
                </SimpleGrid>
              )}

              {measureMode === 'weight' && (
                <SimpleGrid cols={2} spacing="sm">
                  <TextInput label="Weight" type="number" min={0} size="md"
                    value={checkInWeight} onChange={e => setCheckInWeight(e.target.value)}
                    placeholder="e.g. 1250" />
                  <Select label="Unit" size="md"
                    data={[{ value: 'lbs', label: 'Pounds (lbs)' }, { value: 'tons', label: 'Tons' }]}
                    value={checkInWeightUnit}
                    onChange={v => setCheckInWeightUnit(v ?? 'lbs')} />
                </SimpleGrid>
              )}

              {/* Load photo capture */}
              <div>
                <Text size="sm" fw={600} mb={4}>Load Photo</Text>
                {loadPhotoData ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={loadPhotoData.data} alt="Load"
                      style={{ width: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover' }} />
                    <ActionIcon size="sm" color="red" variant="filled"
                      style={{ position: 'absolute', top: 4, right: 4 }}
                      onClick={() => setLoadPhotoData(null)}>
                      <IconX size={12} />
                    </ActionIcon>
                  </div>
                ) : (
                  <label style={{ cursor: 'pointer' }}>
                    <Paper p="md" radius="sm" withBorder
                      style={{ textAlign: 'center', background: 'var(--mantine-color-gray-0)' }}>
                      <IconCamera size={24} color="#888" />
                      <Text size="xs" c="dimmed" mt={4}>Tap to photograph the load</Text>
                    </Paper>
                    <input type="file" accept="image/*" capture="environment"
                      style={{ display: 'none' }}
                      onChange={e => handleLoadPhoto(e.target.files?.[0] ?? null)} />
                  </label>
                )}
              </div>

              <Select label="Monitor" data={monitorOptions} value={selectedMonitor}
                onChange={v => setSelectedMonitor(v)} searchable size="md"
                placeholder="Select your name..." />

              <Textarea label="Notes" value={monitorNotes}
                onChange={e => setMonitorNotes(e.target.value)}
                placeholder="Any observations about the load..."
                autosize minRows={2} />

              <Button size="lg" fullWidth leftSection={<IconCheck size={18} />}
                loading={saving} onClick={() => setConfirmOpen(true)}
                disabled={!selectedTruck || !debrisType}>
                Check In Truck —{' '}
                {measureMode === 'volumetric' ? `${calculatedCY.toFixed(1)} CY`
                  : measureMode === 'unit' ? `${unitCount || 0} ${debrisCfg?.unitLabel || 'units'}`
                  : `${checkInWeight || 0} ${checkInWeightUnit}`}
              </Button>
            </Stack>
          </Paper>

          {/* Recent check-ins at this site */}
          <Paper p="lg" radius="md" withBorder>
            <Text fw={700} mb="md">Recent Activity at {activeSite?.siteName}</Text>
            {siteTickets.length === 0 ? (
              <Text c="dimmed" size="sm">No check-ins yet</Text>
            ) : (
              <Stack gap="xs">
                {siteTickets.map(t => {
                  const isVerified = t.status === 'verified';
                  const isDelivered = t.status === 'delivered';
                  const isDisputed = t.status === 'disputed';
                  const isVoided = t.status === 'voided';
                  return (
                    <Paper key={t.id} p="xs" radius="sm" withBorder>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconTruck size={16} />
                          <div>
                            <Text size="sm" fw={500}>{t.truckNumber} — {t.ticketNumber}</Text>
                            <Text size="xs" c="dimmed">
                              {DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType} &middot;{' '}
                              {t.calculatedCubicYards ? `${parseFloat(t.calculatedCubicYards).toFixed(1)} CY` : t.unitCount ? `${t.unitCount} units` : t.weight ? `${t.weight} ${t.weightUnit || 'lbs'}` : '-'}
                            </Text>
                          </div>
                        </Group>
                        <Group gap={6}>
                          <div style={{ textAlign: 'right' }}>
                            <Text size="xs" c="dimmed">{t.verifiedAt ? new Date(t.verifiedAt).toLocaleTimeString() : t.disposalTime || t.pickupTime || ''}</Text>
                          </div>
                          {/* Verified → can mark delivered */}
                          {isVerified && (
                            <Button size="compact-xs" variant="light" color="green"
                              leftSection={<IconShieldCheck size={12} />}
                              onClick={async () => {
                                await updateLoadTicket(t.id, { status: 'delivered' as any, disposalTime: new Date().toISOString() });
                                notifications.show({ message: `Ticket ${t.ticketNumber} marked delivered`, color: 'green' });
                                loadLoadTickets();
                              }}>
                              Delivered
                            </Button>
                          )}
                          {/* Verified or delivered → can dispute/void */}
                          {(isVerified || isDelivered) && (
                            <Group gap={4}>
                              <Button size="compact-xs" variant="light" color="red"
                                onClick={async () => {
                                  await updateLoadTicket(t.id, { status: 'disputed' as any, notes: `${t.notes ? t.notes + ' | ' : ''}Disputed by monitor at ${new Date().toLocaleTimeString()}` });
                                  notifications.show({ message: `Ticket ${t.ticketNumber} disputed`, color: 'orange' });
                                  loadLoadTickets();
                                }}>
                                Dispute
                              </Button>
                              <Button size="compact-xs" variant="light" color="gray"
                                onClick={async () => {
                                  await updateLoadTicket(t.id, { status: 'voided' as any, notes: `${t.notes ? t.notes + ' | ' : ''}Voided by monitor at ${new Date().toLocaleTimeString()}` });
                                  notifications.show({ message: `Ticket ${t.ticketNumber} voided`, color: 'red' });
                                  loadLoadTickets();
                                }}>
                                Void
                              </Button>
                            </Group>
                          )}
                          {/* Disputed → can re-verify or void */}
                          {isDisputed && (
                            <Group gap={4}>
                              <Button size="compact-xs" variant="light" color="blue"
                                onClick={() => window.open(`/debris/verify/${t.id}`, '_blank')}>
                                Re-Verify
                              </Button>
                              <Button size="compact-xs" variant="light" color="gray"
                                onClick={async () => {
                                  await updateLoadTicket(t.id, { status: 'voided' as any, notes: `${t.notes ? t.notes + ' | ' : ''}Voided after dispute at ${new Date().toLocaleTimeString()}` });
                                  notifications.show({ message: `Ticket ${t.ticketNumber} voided`, color: 'red' });
                                  loadLoadTickets();
                                }}>
                                Void
                              </Button>
                            </Group>
                          )}
                          {/* Voided → admin can undo */}
                          {isVoided && isAdmin && (
                            <Button size="compact-xs" variant="subtle" color="gray"
                              onClick={async () => {
                                await updateLoadTicket(t.id, { status: 'disputed' as any, notes: `${t.notes ? t.notes + ' | ' : ''}Void reversed by admin at ${new Date().toLocaleTimeString()}` });
                                notifications.show({ message: `Ticket ${t.ticketNumber} restored — needs re-verification`, color: 'blue' });
                                loadLoadTickets();
                              }}>
                              Undo Void
                            </Button>
                          )}
                          <Badge size="xs" variant="light"
                            color={TICKET_STATUS_COLORS[t.status as keyof typeof TICKET_STATUS_COLORS] || 'gray'}>
                            {t.status?.replace(/_/g, ' ')}
                          </Badge>
                        </Group>
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </SimpleGrid>
      )}

      {/* Confirm check-in dialog */}
      <Modal opened={confirmOpen} onClose={() => setConfirmOpen(false)} title="Confirm Check-In" centered size="sm">
        <Stack>
          <Text size="sm">
            Check in <strong>{activeTruck?.truckNumber}</strong> at <strong>{activeSite?.siteName}</strong>?
          </Text>
          <Text size="xs" c="dimmed">
            This will create a verified ticket immediately since you are the on-site monitor.
          </Text>
          <SimpleGrid cols={2}>
            <div><Text size="xs" c="dimmed">Debris Type</Text><Text fw={600}>{debrisType ? (DEBRIS_TYPE_LABELS[debrisType as DebrisType] ?? debrisType) : '-'}</Text></div>
            <div>
              <Text size="xs" c="dimmed">Measurement</Text>
              <Text fw={600}>
                {measureMode === 'volumetric' ? `${loadPct}% — ${calculatedCY.toFixed(1)} CY`
                  : measureMode === 'unit' ? `${unitCount || 0} ${debrisCfg?.unitLabel || 'units'}${unitSubcategory ? ` (${unitSubcategory})` : ''}`
                  : `${checkInWeight || 0} ${checkInWeightUnit}`}
              </Text>
            </div>
          </SimpleGrid>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button size="xs" loading={saving} onClick={() => { setConfirmOpen(false); handleCheckIn(); }}>
              Confirm Check-In
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
